/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useState, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { get, isArray, isNil } from 'lodash';

import { AssetDropzoneFieldProps } from '@broadleaf/admin-components/dist/form/named-components/field-types/AssetDropzoneField';
import {
  IconButton,
  log,
  mapper
} from '@broadleaf/admin-components/dist/common';
import useTranslateMode from '@broadleaf/admin-components/dist/form/hooks/useTranslateMode';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import {
  FieldDecorations,
  useAssetDropzoneField
} from '@broadleaf/admin-components/dist/form';
import { openModal } from '@broadleaf/admin-components/dist/form/item-browser/actions/itemBrowser';
import { ItemBrowserProvider } from '@broadleaf/admin-components/dist/form/item-browser/providers/ItemBrowserProvider';
import { ItemBrowserAssignModal } from '@broadleaf/admin-components/dist/form/item-browser/components/ItemBrowserModals/components/ItemBrowserAssignModal';

import messages from '@broadleaf/admin-components/dist/form/named-components/field-types/AssetDropzoneField/AssetDropzoneField.messages.js';
import PDFAssetDropzone from './PDFAssetDropzone';
import useItemBrowser from '@broadleaf/admin-components/dist/form/item-browser/hooks/useItemBrowser';
import { unstable } from '@broadleaf/admin-components';

const { MetadataUtils } = unstable;
const { findAction, findComponent, getAttribute, isFieldDisabled } =
  MetadataUtils;

const logger = log.getLogger(
  'form.named-components.field-types.PDFAssetDropzoneField'
);

/**
 * The field component that handles uploading an Asset and set its url to the form state.
 * It also allow user to target a specific URL as text.
 *
 * @visibleName ASSET_DROPZONE
 */
export const PDFAssetDropzoneField: React.FC<
  AssetDropzoneFieldProps
> = props => {
  const { formik, metadata, ...otherProps } = props;
  const [showSource, setShowSource] = useState(false);
  const translateMode = useTranslateMode();
  const formatMessage = useFormatMessage();
  const disabled = isFieldDisabled(props, translateMode);

  const error = get(formik.errors, metadata.name);
  const touched = get(formik.touched, metadata.name, false);

  const decorated = props.decorated || metadata.decorated;
  const label = metadata.label;
  const placeholder = metadata.placeholder;
  const maxWidth = getAttribute(metadata, 'maxWidth', 300);
  const maxHeight = getAttribute(metadata, 'maxHeight', 300);
  const ref = useRef<HTMLDivElement>(null);

  const {
    accept,
    asset,
    clearValue,
    handleChange,
    handleSelection,
    handleUpload,
    init,
    onRejectFile,
    srcKey,
    typeKey
  } = useAssetDropzoneField({ formik, metadata });

  useEffect(
    () => {
      init();
    },
    [] // eslint-disable-line
  );

  useEffect(() => {
    if (ref.current) {
      ref.current.style.maxWidth = maxWidth;
    }
  }, [maxWidth]);

  const dropZoneProps = {
    accept,
    disabled,
    emptyMessage: getAttribute(
      metadata,
      'emptyMessage',
      'Drag and drop a file here or click'
    ),
    handleUpload,
    multiple: false,
    onRejectFile,
    srcKey,
    typeKey
  };

  return (
    <div
      ref={ref}
      className={cx('AssetDropzoneField tw-relative', props.className, {
        disabled: !!disabled
      })}
    >
      <div
        className={cx('tw-z-10 tw-flex tw-w-full tw-justify-end tw-px-1', {
          'tw-absolute tw-top-[-0.5rem]': metadata.label
        })}
      >
        <PickExistingAsset
          disabled={disabled}
          onPickExistingAssetSuccess={handleSelection}
          {...props}
        />
        <IconButton
          className="AssetDropzoneField__btn--toggle--source tw-text-gray-600"
          iconClassName="fa-lg"
          disabled={disabled}
          name={showSource ? 'toggle-on' : 'toggle-off'}
          onClick={e => {
            e.preventDefault();
            setShowSource(!showSource);
          }}
          label={formatMessage(messages.toggleSource)}
        />

        <IconButton
          disabled={!asset || disabled}
          className={
            'AssetDropzoneField__btn--delete hover:tw-no-underline focus:tw-no-underline'
          }
          name="trash"
          onClick={clearValue}
          label={formatMessage(messages.deleteBtnTitle)}
        />
      </div>

      <FieldDecorations
        fullWidth={maxWidth === '100%'}
        formik={formik}
        metadata={metadata}
        {...otherProps}
      >
        {!showSource && (
          <PDFAssetDropzone
            className="AssetDropzoneField__asset--drop--zone tw-mt-2 tw-min-h-[200px] tw-min-w-[200px]"
            {...dropZoneProps}
            style={{ width: maxWidth, height: maxHeight, maxWidth, maxHeight }}
            value={asset}
          />
        )}

        {showSource && (
          <input
            autoComplete="off"
            className={cx(
              'form-control',
              props.className,
              {
                'is-invalid': touched && !!error
              },
              'AssetDropzoneField__asset--source tw-mt-2'
            )}
            type="text"
            disabled={disabled}
            name={metadata.name}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            placeholder={!decorated && !placeholder ? label : placeholder}
            value={get(asset, srcKey, '')}
          />
        )}
      </FieldDecorations>
    </div>
  );
};

PDFAssetDropzoneField.defaultProps = {
  disabled: false
};

const PickExistingAsset: React.FC<
  AssetDropzoneFieldProps & { onPickExistingAssetSuccess?: Function }
> = ({ metadata, onPickExistingAssetSuccess, disabled }) => {
  const assignActionDefinition = useMemo(
    () => findAction(metadata, { type: 'ASSIGN' }),
    [metadata]
  );

  const enablePickExistingAsset = !isNil(assignActionDefinition);

  const idKey = getAttribute(metadata, 'idKey', 'id');

  const itemBrowser = useItemBrowser({
    idKey
  });

  const openItemBrowserModal = e => {
    e.preventDefault();
    e.stopPropagation();

    itemBrowser.dispatch(
      openModal('ASSIGN', {
        actionDefinition: assignActionDefinition
      })
    );
  };

  const handlePickExistingAsset = values => {
    return new Promise(resolve => {
      const assetLookupComponent = findComponent(assignActionDefinition, {
        type: 'ASSET_BROWSER_LOOKUP'
      });
      let item = get(values, assetLookupComponent.name);

      const transformSelection = getTransformSelectionMappings(
        assignActionDefinition
      );
      if (isArray(transformSelection)) {
        item = mapper.transform(item, transformSelection);
      }

      onPickExistingAssetSuccess(item);
      return resolve(item);
    });
  };

  const modalState = itemBrowser.state.modals.ASSIGN;

  return (
    // @ts-ignore
    <ItemBrowserProvider
      dispatch={itemBrowser.dispatch}
      state={itemBrowser.state}
    >
      {enablePickExistingAsset && (
        <IconButton
          className={'AssetDropzoneField__button--pick--existing--asset'}
          disabled={disabled}
          label={assignActionDefinition.label}
          name="images"
          onClick={openItemBrowserModal}
        />
      )}
      {!isNil(modalState) && (
        <ItemBrowserAssignModal
          handleAssign={handlePickExistingAsset}
          {...modalState.modalProps}
        />
      )}
    </ItemBrowserProvider>
  );
};

/**
 * Helper function used to the the correct mappings for the option selection transformation.
 *
 * @param metadata the metadata
 * @returns {Array}
 */
function getTransformSelectionMappings(metadata) {
  const transformSelection = getAttribute(metadata, 'transformSelection', null);
  if (isArray(transformSelection)) {
    return transformSelection;
  }

  const deprecatedTransformSelectedValue = getAttribute(
    metadata,
    'transformSelectValue',
    null
  );
  if (isArray(deprecatedTransformSelectedValue)) {
    logger.warn(
      'Found usage of deprecated `transformSelectedValue` within ASSET_DROPZONE field. Please use `transformSelection` instead.'
    );
    return deprecatedTransformSelectedValue;
  }

  return [];
}

export default PDFAssetDropzoneField;
